import { ArPhotoButton } from '@/components/Pages/spots/components/ArPhotoButton'
import { AudioGuideButton } from '@/components/Pages/spots/components/AudioGuideButton'
import { SheetName } from '@/components/Pages/spots/components/SheetName'
import { TagBadge } from '@/components/Pages/spots/components/TagBadge'
import type { SpotData } from '@/schemas/v2/spot'
import { IconChevronRight } from '@tabler/icons-react'
import Link from 'next/link'

type Props = {
  spot: SpotData
  /** スポット詳細ページへのパス（例: `/uji/spot/1`） */
  href: string
  /** タグ一覧（例: ['観光', '歴史', 'その他']）*/
  tags?: string[]
  /** 所属シート名一覧（例: ['京都観光コース', '歴史探索コース']） */
  sheetNames?: string[]
  /** 音声ガイドの有無 */
  hasAudioGuide?: boolean
  /** 音声ガイドボタン押下時のコールバック（スポット詳細と同様に DialogSpotsAudioGuide を開く） */
  onAudioGuideClick?: () => void
  /** 有効範囲外のとき音声ガイドボタンを鍵アイコンにする */
  audioGuideLocked?: boolean
  /** 音声ガイドがアクティブ（primaryカラー表示）かどうか */
  audioGuideActive?: boolean
  /** ARフォトの有無 */
  hasArPhoto?: boolean
  /** ARフォトボタン押下時のコールバック（スポット詳細と同様に ArPhotoOverlay を開く） */
  onArPhotoClick?: () => void
  /** スタンプ未取得または有効範囲外のとき ARフォトボタンを鍵アイコンにする */
  arPhotoLocked?: boolean
  /** ARフォトがアクティブ（primaryカラー表示）かどうか */
  arPhotoActive?: boolean
  /** スポット名を非表示にする（Drawerタイトルに表示済みの場合に使用） */
  hideName?: boolean
  /** 位置情報取得中はスケルトン表示にして disabled にする */
  geoLoading?: boolean
}

/**
 * /spots にて表示される次の場所にて利用される
 * - map状態の下から出てくるModal内のコンポーネント
 * - list状態のコンポーネント
 * */
export function SpotOverview({
  spot,
  href,
  tags,
  sheetNames,
  hasAudioGuide,
  onAudioGuideClick,
  audioGuideLocked = false,
  audioGuideActive = false,
  hasArPhoto,
  onArPhotoClick,
  arPhotoLocked = false,
  arPhotoActive = false,
  hideName = false,
  geoLoading = false,
}: Props) {
  return (
    <Link href={href} className="flex items-start gap-3 px-4 py-4 min-h-[132px]">
      {/* サムネイル */}
      <div className="relative size-[100px] shrink-0 self-start">
        {spot.images[0] ? (
          <img src={spot.images[0]} alt={spot.name} className="size-full rounded-[16px] object-cover" />
        ) : (
          <div className="size-full rounded-[16px] bg-gray-100" aria-hidden="true" />
        )}
      </div>

      {/* テキスト情報 */}
      <div className="min-w-0 flex-1 flex flex-col self-stretch">
        {/* スポット名 */}
        {!hideName && <p className="text-[16px] font-bold leading-[24px] text-black line-clamp-2">{spot.name}</p>}

        {/* シート名バッジ */}
        {sheetNames && sheetNames.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {sheetNames.map((name, idx) => (
              <SheetName key={idx} name={name} />
            ))}
          </div>
        )}

        {/* クーポンバッジ（クーポンが設定されている場合のみ表示） */}
        {spot.coupon && (
          <div className="mt-1 flex flex-wrap gap-1">
            <span className="rounded-[8px] border border-[#ff8a00] px-2 py-0.5 text-[11px] font-bold leading-[16px] text-black">
              クーポン
            </span>
          </div>
        )}

        {/* タグバッジ（下部に配置） */}
        {tags && tags.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1 pt-1">
            {tags.map((tag, idx) => (
              <TagBadge key={idx} tag={tag} />
            ))}
          </div>
        )}
      </div>

      {/* ARフォト・音声ガイドボタン（ある場合のみ表示） */}
      {(hasArPhoto || hasAudioGuide) && (
        <div
          className="flex shrink-0 items-center gap-1 self-center"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {hasArPhoto && (
            <ArPhotoButton
              active={arPhotoActive}
              locked={arPhotoLocked}
              loading={geoLoading}
              onClick={onArPhotoClick}
            />
          )}
          {hasAudioGuide && (
            <AudioGuideButton
              active={false}
              locked={audioGuideLocked}
              loading={geoLoading}
              onClick={onAudioGuideClick}
            />
          )}
        </div>
      )}

      {/* 矢印（常に右端） */}
      <IconChevronRight size={24} stroke={1.5} className="shrink-0 self-center text-gray-400" aria-hidden="true" />
    </Link>
  )
}
